export interface MaintenancePlan {
  id: string;
  name: string;
  icon: "Wrench" | "ShieldCheck" | "Trophy";
  description: string;
  price: string;
  priceSuffix: string | null;
  priceNote: string;
  paymentNote: string | null;
  badge: string | null;
  highlight: boolean;
  buttonText: string;
  items: string[];
}

export interface MaintenancePlansConfig {
  sectionLabel: string;
  sectionTitle: string;
  sectionSubtitle: string;
  plans: MaintenancePlan[];
}

export const defaultMaintenancePlans: MaintenancePlansConfig = {
  sectionLabel: "Service Packages",
  sectionTitle: "Maintenance Plans Built for Winnipeg Drivers.",
  sectionSubtitle: "Straightforward pricing. No surprises. We come to you.",
  plans: [
    {
      id: "basic",
      name: "Basic",
      icon: "Wrench",
      description: "Essential protection for daily drivers",
      price: "89.99",
      priceSuffix: "+ tax",
      priceNote: "per visit · billed per service",
      paymentNote: "We accept debit and credit cards",
      badge: null,
      highlight: false,
      buttonText: "Get Started",
      items: [
        "Full synthetic or conventional oil change",
        "Oil filter replacement",
        "Tyre pressure check & top-up",
        "Visual safety inspection (20-point)",
        "Windshield washer fluid top-up",
        "Service reminder follow-up",
      ],
    },
    {
      id: "standard",
      name: "Standard",
      icon: "ShieldCheck",
      description: "Complete care for everyday peace of mind",
      price: "159.99",
      priceSuffix: "+ tax",
      priceNote: "per visit · billed per service",
      paymentNote: "We accept debit and credit cards",
      badge: "Most Popular",
      highlight: true,
      buttonText: "Get Started",
      items: [
        "Everything in Basic",
        "Tyre rotation & balance check",
        "Brake inspection (pads & rotors)",
        "Air filter inspection & replacement",
        "Battery health test",
        "Fluid top-ups (coolant, brake, power steering)",
        "Wiper blade check & replacement",
        "Priority scheduling",
      ],
    },
    {
      id: "premium",
      name: "Premium",
      icon: "Trophy",
      description: "Full-service protection, nothing overlooked",
      price: "249.99",
      priceSuffix: "+ tax",
      priceNote: "per visit · billed per service",
      paymentNote: "We accept debit and credit cards",
      badge: null,
      highlight: false,
      buttonText: "Get Started",
      items: [
        "Everything in Standard",
        "Full diagnostic scan (OBD-II)",
        "Spark plug inspection & replacement",
        "Cabin air filter replacement",
        "Fuel system check",
        "Transmission fluid inspection",
        "Belt & hose inspection",
        "Detailed written service report",
        "Same-day emergency call-out (1x/year)",
      ],
    },
  ],
};

export interface SiteConfig {
  phone: {
    display: string;
    tel: string;
    whatsapp: string;
  };
  whatsapp: {
    bookingUrl: string;
    quoteUrl: string;
    contactUrl: string;
  };
  hours: string;
  serviceArea: string;
  serviceAreaShort: string;
}

export interface SiteImages {
  van: string;
  vanMobile: string;
  vanDesktop: string;
  vanWinter: string;
  vanWinterOptimized: string;
  engineBay: string;
  engineBayOptimized: string;
  brakeRotor: string;
  brakeRotorOptimized: string;
  suspension: string;
  suspensionOptimized: string;
  electricalMechanic: string;
  tireBrakeMechanic: string;
  hvacService: string;
}

export const defaultConfig: SiteConfig = {
  phone: {
    display: "431 688-4950",
    tel: "4316884950",
    whatsapp: "14316884950",
  },
  whatsapp: {
    bookingUrl:
      "https://wa.me/14316884950?text=Hi%2C%20I%27d%20like%20to%20book%20a%20mobile%20mechanic%20service%20in%20Winnipeg.",
    quoteUrl:
      "https://wa.me/14316884950?text=Hi%2C%20I%27d%20like%20to%20get%20a%20quote%20for%20mobile%20mechanic%20service%20in%20Winnipeg.",
    contactUrl:
      "https://wa.me/14316884950?text=Hi%2C%20I%27d%20like%20to%20get%20a%20quote%20for%20mobile%20mechanic%20service%20in%20Winnipeg.",
  },
  hours: "10 AM – 10 PM",
  serviceArea: "Winnipeg & Surrounding Areas",
  serviceAreaShort: "Winnipeg",
};

/**
 * Returns default image paths correctly prefixed with the Vite base URL.
 * BASE_URL is baked in at build time ("/" for root-domain deployment).
 * This ensures images load correctly even before images.json is fetched.
 */
export function getDefaultImages(): SiteImages {
  const base = import.meta.env.BASE_URL; // "/" in production build
  return {
    van: `${base}van-desktop.webp`,
    vanMobile: `${base}van-mobile.webp`,
    vanDesktop: `${base}van-desktop.webp`,
    vanWinter: `${base}van-winter-600.webp`,
    vanWinterOptimized: `${base}van-winter-600.webp`,
    engineBay: `${base}engine-bay-800.webp`,
    engineBayOptimized: `${base}engine-bay-800.webp`,
    brakeRotor: `${base}brake-rotor-600.webp`,
    brakeRotorOptimized: `${base}brake-rotor-600.webp`,
    suspension: `${base}suspension-600.webp`,
    suspensionOptimized: `${base}suspension-600.webp`,
    electricalMechanic: `${base}electrical-mechanic-800.webp`,
    tireBrakeMechanic: `${base}tire-brake-mechanic-800.webp`,
    hvacService: `${base}hvac-service-800.webp`,
  };
}

// Kept for backward compatibility — not used for image paths
export const defaultImages: SiteImages = {
  van: "",
  vanMobile: "",
  vanDesktop: "",
  vanWinter: "",
  vanWinterOptimized: "",
  engineBay: "",
  engineBayOptimized: "",
  brakeRotor: "",
  brakeRotorOptimized: "",
  suspension: "",
  suspensionOptimized: "",
  electricalMechanic: "",
  tireBrakeMechanic: "",
  hvacService: "",
};
